import React, {FC, useState, useEffect} from 'react'
import {showSwal} from '../../../../ui-kit'
import {useParams, useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner} from 'react-bootstrap'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {OecdDescription} from '../oecd-descriptions/core/_models'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {
  getOecdDescriptionById,
  createOecdDescription,
  updateOecdDescriptionById,
} from '../oecd-descriptions/core/_requests'
import OecdDescriptionsDetailView from './components/OecdDescriptionsDetailView'

const OecdDescriptionsDetailPage: FC<{subject: string}> = ({subject}) => {
  const {idOecdDescription} = useParams<{idOecdDescription: string}>()
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const ability = useAbility(AbilityContext)

  const isAddMode = !idOecdDescription
  const isDetailMode = !!idOecdDescription
  const [isEditMode, setIsEditMode] = useState(isAddMode)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)

  const [oecdDescriptionDetail, setOecdDescriptionDetail] = useState<OecdDescription>({
    code: '',
    desc: null,
  })

  useEffect(() => {
    const loadDetail = async () => {
      if (!isDetailMode || !idOecdDescription) return
      setIsLoading(true)
      try {
        const detailRes = await getOecdDescriptionById(axiosPrivate, idOecdDescription)
        const detailData = detailRes.data.data
        if (detailData) {
          setOecdDescriptionDetail({
            code: detailData.code || '',
            desc: detailData.desc || null,
          })
        }
      } catch (err) {
        console.error('Failed to load OECD description detail:', err)
        showSwal('Error', 'Failed to load OECD description details.', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadDetail()
  }, [idOecdDescription])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setOecdDescriptionDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!oecdDescriptionDetail.code.trim()) {
      showSwal('Warning', 'Code is required.', 'warning')
      return
    }

    showSwal({
      title: `Are you sure you want to ${isAddMode ? 'create' : 'update'} this OECD Description?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)
      const payload = {
        code: oecdDescriptionDetail.code.trim(),
        desc: oecdDescriptionDetail.desc?.trim() || null,
      }

      if (isAddMode) {
        createOecdDescription(axiosPrivate, payload)
          .then((res) => {
            if (res.status === 201) {
              showSwal('Data created', undefined, 'success').then(() => {
                navigate('/oecd-descriptions')
              })
            }
          })
          .catch((err) => {
            console.error('Failed to create OECD description:', err)
            showSwal('Error', 'Failed to create OECD description.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        updateOecdDescriptionById(axiosPrivate, idOecdDescription, payload)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data updated', undefined, 'success').then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error('Failed to update OECD description:', err)
            showSwal('Error', 'Failed to update OECD description.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/oecd-descriptions')
        } else {
          setIsEditMode(false)
          window.location.reload()
        }
      }
    })
  }

  const isFormValid = !!oecdDescriptionDetail.code.trim()

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'OECD Descriptions',
            path: '/oecd-descriptions',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode
          ? 'Add OECD Description'
          : isEditMode
          ? `Edit OECD Description: ${oecdDescriptionDetail.code}`
          : `OECD Description Detail`}
      </PageTitle>

      <div className='OecdDescriptionsDetailPage'>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-list-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isAddMode
                          ? 'Add New OECD Description'
                          : isEditMode
                          ? 'Edit OECD Description Details'
                          : 'OECD Description Detail'}
                      </h3>
                    </div>
                    {ability.can('update', subject) && isDetailMode && !isEditMode && (
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    )}
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoading ? (
                  isEditMode ? (
                    <Form onSubmit={handleSubmit} className='form-oecd-descriptions'>
                      <div className='row mb-4'>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Code <span className='text-danger'>*</span>
                          </Form.Label>
                          <Form.Control
                            placeholder='Enter OECD code'
                            type='text'
                            name='code'
                            value={oecdDescriptionDetail.code}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>Description</Form.Label>
                          <Form.Control
                            as='textarea'
                            rows={4}
                            placeholder='Enter description'
                            name='desc'
                            value={oecdDescriptionDetail.desc || ''}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            type='submit'
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : (
                    <OecdDescriptionsDetailView oecdDescriptionDetail={oecdDescriptionDetail} />
                  )
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default OecdDescriptionsDetailPage
